import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Chip,
  Button,
  Stack,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import Tooltip from '../shared/LibraryTooltip';
import SafeMarkdown from '../shared/SafeMarkdown';
import { contextViewDialogPaperSx } from '../shared/prompt-display/expandedPromptShellPaperSx';
import { contextContentCardSx } from '../../constants/panelSurfaces';
import { contextContentMarkdownBodySx } from '../shared/markdownBodySx';
import { getContext } from '../../services/api';
import {
  Share as ShareIcon,
  Close as CloseIcon,
  EditOutlined as EditIcon,
  FullscreenOutlined as FullscreenIcon,
  FullscreenExitOutlined as FullscreenExitIcon,
  MoreVertOutlined as MoreVertIcon,
  StarBorderOutlined as FavoriteIcon,
  Star as FavoritedIcon,
  DeleteOutlineOutlined as DeleteIcon,
  AdminPanelSettingsOutlined as ManagePermissionsIcon
} from '@mui/icons-material';

const contextContentCardBodySx = {
  ...contextContentCardSx,
};

const ContextDialog = ({
  open,
  context,
  onClose,
  onEdit,
  canEdit,
  onSelect,
  isSelected,
  onFavorite,
  onShare,
  onDelete,
  onManagePermissions,
  canDelete = false,
  canManagePermissions = false,
  showShareAction = true,
  showFavoriteAction = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [fullScreenOpen, setFullScreenOpen] = useState(false);
  const [displayContext, setDisplayContext] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);

  // When context is from list, it may not include content (e.g. S3-stored). Fetch full context by ID.
  useEffect(() => {
    if (!open || !context) {
      setDisplayContext(null);
      setLoadingContent(false);
      return;
    }
    const hasContent = context.content != null && String(context.content).trim() !== '';
    if (hasContent) {
      setDisplayContext(context);
      setLoadingContent(false);
      return;
    }
    if (!context.id) {
      setDisplayContext(context);
      return;
    }
    setLoadingContent(true);
    getContext(context.id)
      .then((res) => {
        const full = res?.context || res?.data || res;
        if (full && full.id) {
          setDisplayContext({ ...context, ...full, content: full.content });
        } else {
          setDisplayContext(context);
        }
      })
      .catch(() => {
        setDisplayContext(context);
      })
      .finally(() => {
        setLoadingContent(false);
      });
    // We intentionally omit context from dependencies to avoid re-fetch loops when displayContext changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, context?.id]);

  const ctx = displayContext || context;
  const showHeaderMenu = !onSelect && (
    (showFavoriteAction && !!onFavorite) ||
    (canManagePermissions && !!onManagePermissions) ||
    (showShareAction && !!onShare) ||
    (canDelete && !!onDelete)
  );

  if (!context) return null;

  const handleSelect = () => {
    if (onSelect) {
      onSelect(ctx);
      onClose();
    }
  };

  const handleExpand = () => {
    setFullScreenOpen(true);
  };

  const handleCloseFullScreen = () => {
    setFullScreenOpen(false);
  };

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        // When used inside ContextSelector (onSelect present), don't allow backdrop/escape to close
        // Only allow explicit close button clicks
        if (onSelect && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        onClose();
      }}
      maxWidth="xl" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          ...contextViewDialogPaperSx,
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {ctx.name || 'Untitled knowledge'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {canEdit && onEdit && (
              <Tooltip title="Edit knowledge">
                <IconButton 
                  size="small"
                  onClick={() => {
                    // Only close if we're not in a nested dialog (when onSelect is present, we're in ContextSelector)
                    // ContextSelector's handleEditContext will handle closing the preview dialog
                    if (!onSelect) {
                      onClose();
                    }
                    onEdit(ctx);
                  }}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { 
                      color: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {showHeaderMenu && (
              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  onClick={(event) => setHeaderMenuAnchor(event.currentTarget)}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <Menu
        anchorEl={headerMenuAnchor}
        open={Boolean(headerMenuAnchor) && showHeaderMenu}
        onClose={() => setHeaderMenuAnchor(null)}
      >
        {showFavoriteAction && onFavorite && (
          <MenuItem onClick={() => { onFavorite(ctx?.id); setHeaderMenuAnchor(null); }}>
            <ListItemIcon>{ctx?.isFavorited ? <FavoritedIcon fontSize="small" sx={{ color: '#d4a017' }} /> : <FavoriteIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>{ctx?.isFavorited ? 'Remove from favorites' : 'Add to favorites'}</ListItemText>
          </MenuItem>
        )}
        {canManagePermissions && onManagePermissions && (
          <MenuItem onClick={() => { onManagePermissions(ctx); setHeaderMenuAnchor(null); }}>
            <ListItemIcon><ManagePermissionsIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Manage permissions</ListItemText>
          </MenuItem>
        )}
        {showShareAction && onShare && (
          <MenuItem onClick={() => { onShare(ctx); setHeaderMenuAnchor(null); }}>
            <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Share knowledge</ListItemText>
          </MenuItem>
        )}
        {canDelete && onDelete && (
          <MenuItem onClick={() => { onDelete(ctx); setHeaderMenuAnchor(null); }}>
            <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Delete knowledge</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <DialogContent sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Box>
          {/* Description */}
          <Typography variant="body1" sx={{ mb: 2 }}>
            {ctx.description || 'No description'}
          </Typography>

          {/* Context Content */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">Content</Typography>
            <Tooltip title="Expand to full screen">
              <IconButton 
                size="small"
                onClick={handleExpand}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { 
                    color: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={contextContentCardBodySx}>
            {loadingContent ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <SafeMarkdown markdownBaseSx={contextContentMarkdownBodySx}>
                {ctx.content || 'No content'}
              </SafeMarkdown>
            )}
          </Box>

          {/* Tags at the bottom */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Tags</Typography>
            {ctx.tags && ctx.tags.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {ctx.tags.map((tag, i) => (
                  <Chip key={i} label={tag} size="small" variant="outlined" />
                ))}
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary">No tags</Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          flexShrink: 0,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 0 }
        }}
      >
        <Button onClick={onClose} size={isMobile ? 'small' : 'medium'} fullWidth={isMobile}>
          Close
        </Button>
        {onSelect && (
          <Button
            variant="contained"
            onClick={handleSelect}
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            {isSelected ? 'Deselect' : 'Select'} Knowledge
          </Button>
        )}
      </DialogActions>

      {/* Full Screen Modal */}
      <Dialog
        open={fullScreenOpen}
        onClose={handleCloseFullScreen}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: '95vw' },
            height: { xs: '100dvh', sm: '95vh' },
            maxWidth: { xs: '100vw', sm: '95vw' },
            maxHeight: { xs: '100dvh', sm: '95vh' },
            m: { xs: 0, sm: 1 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }
        }}
      >
        {/* Fixed header section with exit button */}
        <DialogTitle sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', py: 1, px: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Tooltip title="Exit full screen">
              <IconButton 
                onClick={handleCloseFullScreen}
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { 
                    color: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <FullscreenExitIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ flex: 1, overflow: 'auto', p: { xs: 2, sm: 3 } }}>
          {/* Only Content - Same styling as regular modal */}
          <Box sx={{ ...contextContentCardBodySx, minHeight: '100%' }}>
            {loadingContent ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <SafeMarkdown markdownBaseSx={contextContentMarkdownBodySx}>
                {ctx.content || 'No content'}
              </SafeMarkdown>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ContextDialog;

